import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import store from 'stores/store'
import {
  TRADE_VALUE_FILTER_KEY,
  TRADER_ADDRESS_FILTER_KEY,
} from 'utils/constants'
import { useLocalStorage } from './useLocalStorageState'
import { SIDE_FILTER_KEY } from '@components/modals/SideFilterFormModal'
import { NumberRangeFilter } from 'utils/formatting'
import { useVenueFilter, VenueFilter } from '@components/trade/useSettings'
import { differenceWith, uniqWith } from 'lodash'
import { milliseconds } from 'date-fns'
import { fetchBirdeyeTokenTrades } from 'apis/birdeye/trades'

export const MAX_RECENT_TRADES_LENGTH = 40 // TODO: move into client

export interface Trade {
  base_token_mint: string
  quote_token_mint: string
  broker: string
  slot: number
  time: number
  signature: string
  price: number
  size: number
  signer: string
  taker_side: 'buy' | 'sell'
}

export type SideStats = { trades: number; volume: number }

export const INITIAL_SIDE_STATS: SideStats = { trades: 0, volume: 0 }

export type SideFilter = {
  buy: boolean
  sell: boolean
}

// Apply Traffic-style filters client-side. Birdeye's /defi/txs/token doesn't
// support side / value / trader / venue filtering on the server, so we fetch
// the latest swaps and trim them here. Exported so the realtime WS path can
// reuse the exact same predicate.
export interface RecentTradeFilters {
  sideFilter: SideFilter
  venueFilter: VenueFilter
  valueFilter: NumberRangeFilter
  addressToFilter: string[]
}

export function tradeMatchesFilters(
  t: Trade,
  filters: RecentTradeFilters,
): boolean {
  const { sideFilter, venueFilter, valueFilter, addressToFilter } = filters

  const enabledSides = (Object.keys(sideFilter) as Array<keyof SideFilter>)
    .filter((k) => sideFilter[k])
  if (enabledSides.length === 1 && t.taker_side !== enabledSides[0]) return false

  const enabledVenues = (Object.keys(venueFilter) as Array<keyof VenueFilter>)
    .filter((k) => venueFilter[k])
  const allVenuesEnabled =
    enabledVenues.length === Object.keys(venueFilter).length
  if (!allVenuesEnabled && !enabledVenues.includes(t.broker as keyof VenueFilter)) {
    return false
  }

  if (valueFilter.USDC.min || valueFilter.USDC.max) {
    const usdValue = t.price * t.size
    if (valueFilter.USDC.min && usdValue < parseFloat(valueFilter.USDC.min)) {
      return false
    }
    if (valueFilter.USDC.max && usdValue > parseFloat(valueFilter.USDC.max)) {
      return false
    }
  }

  if (addressToFilter.length && !addressToFilter.includes(t.signer)) return false

  return true
}

export function applyRecentTradeFilters(
  trades: Trade[],
  filters: RecentTradeFilters,
): Trade[] {
  return trades.filter((t) => tradeMatchesFilters(t, filters))
}

export const fetchRecentTrades = async (
  baseMint: string,
  quoteMint: string,
  sideFilter: SideFilter,
  venueFilter: VenueFilter,
  valueFilter: NumberRangeFilter,
  addressToFilter: string[],
): Promise<Trade[]> => {
  try {
    const trades = await fetchBirdeyeTokenTrades({ baseMint, limit: 50 })
    return applyRecentTradeFilters(trades, {
      sideFilter,
      venueFilter,
      valueFilter,
      addressToFilter,
    })
  } catch (e) {
    console.error(
      `failed to fetch recent trades: baseMint=${baseMint}, quoteMint=${quoteMint}`,
      e,
    )
    return []
  }
}

export function useRecentTrades() {
  const baseMint = store((s) => s.market.baseToken.mint)
  const quoteMint = store((s) => s.market.quoteToken.mint)
  const buyStats = store((s) => s.trades.buyStats)
  const realtimeTrades = store((s) => s.trades.realtimeTrades)
  const sellStats = store((s) => s.trades.sellStats)
  const [addressToFilter] = useLocalStorage(TRADER_ADDRESS_FILTER_KEY)
  const [valueFilter] = useLocalStorage(TRADE_VALUE_FILTER_KEY)
  const [venueFilter] = useVenueFilter()
  const [sideFilter] = useLocalStorage(SIDE_FILTER_KEY)

  const { data: latestFetch = [], isInitialLoading: loadingRecentTrades } =
    useQuery({
      queryKey: [
        'recent-trades',
        baseMint,
        quoteMint,
        sideFilter,
        venueFilter,
        valueFilter,
        addressToFilter,
      ],
      queryFn: async () => {
        const trades = await fetchRecentTrades(
          baseMint,
          quoteMint,
          sideFilter,
          venueFilter,
          valueFilter,
          addressToFilter,
        )

        return uniqWith(
          trades,
          ({ signature: s1 }, { signature: s2 }) => s1 === s2,
        )
      },
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: !!baseMint && !!quoteMint,
      // Polled because the WS proxy is prod-only. Each tick returns ~50 of
      // the latest swaps; we accumulate them below so the table keeps growing
      // history rather than refreshing to whatever the latest poll happened
      // to catch.
      refetchInterval: milliseconds({ seconds: 5 }),
      staleTime: milliseconds({ seconds: 2 }),
      cacheTime: milliseconds({ minutes: 5 }),
      keepPreviousData: true,
    })

  // Reset the accumulator when the pair (or any filter that scopes the query)
  // changes — otherwise stale trades from the previous pair would bleed in.
  const [accumulatedRest, setAccumulatedRest] = useState<Trade[]>([])
  useEffect(() => {
    setAccumulatedRest([])
  }, [baseMint, quoteMint, sideFilter, venueFilter, valueFilter, addressToFilter])

  useEffect(() => {
    if (!latestFetch.length) return
    setAccumulatedRest((prev) => {
      const merged = uniqWith(
        [...latestFetch, ...prev],
        ({ signature: s1 }, { signature: s2 }) => s1 === s2,
      )
      return merged
        .sort((a, b) => b.time - a.time)
        .slice(0, MAX_RECENT_TRADES_LENGTH)
    })
  }, [latestFetch])

  const tradesToShow = useMemo(() => {
    const newRealtimeTrades = differenceWith(
      realtimeTrades,
      accumulatedRest,
      ({ signature: s1 }, { signature: s2 }) => s1 === s2,
    )
    const uniqueNewRealtimeTrades = uniqWith(
      newRealtimeTrades,
      ({ signature: s1 }, { signature: s2 }) => s1 === s2,
    )
    const combinedTrades = [...uniqueNewRealtimeTrades, ...accumulatedRest]

    return combinedTrades.sort((a, b) => b.time - a.time)
  }, [realtimeTrades, accumulatedRest])

  const slicedTrades = tradesToShow.slice(0, MAX_RECENT_TRADES_LENGTH)
  const lastTrade = slicedTrades[0]

  return {
    recentTrades: slicedTrades,
    loading: loadingRecentTrades,
    lastTrade,
    lastTradePrice: lastTrade?.price,
    buys: buyStats.trades,
    sells: sellStats.trades,
    buyVolume: buyStats.volume,
    sellVolume: sellStats.volume,
  }
}
